import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Download, Loader2, Sparkles, Check, RefreshCw } from "lucide-react";
import { ChessAppLayout } from "@/components/ChessAppLayout";

const PIECES = ['King', 'Queen', 'Rook', 'Bishop', 'Knight', 'Pawn'] as const;
const COLORS = ['White', 'Black'] as const;

type PieceKey = `${typeof COLORS[number]}-${typeof PIECES[number]}`;

interface GeneratedPiece {
  image: string;
  piece: string;
  color: string;
}

export default function GeneratePieces() {
  const [generatedPieces, setGeneratedPieces] = useState<Record<string, GeneratedPiece>>({});
  const [isGenerating, setIsGenerating] = useState<string | null>(null);
  const [isBatchGenerating, setIsBatchGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();

  const generatePiece = async (piece: string, color: string): Promise<GeneratedPiece | null> => {
    const key = `${color}-${piece}`;
    setIsGenerating(key);
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-chess-piece', {
        body: { piece, color }
      });

      if (error) throw error;
      
      if (data.error) {
        throw new Error(data.error);
      }

      const generated: GeneratedPiece = {
        image: data.image,
        piece: data.piece,
        color: data.color
      };

      setGeneratedPieces(prev => ({ ...prev, [key]: generated }));
      return generated;
    } catch (error: any) {
      console.error('Error generating piece:', error);
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate piece",
        variant: "destructive"
      });
      return null;
    } finally {
      setIsGenerating(null);
    }
  };

  const generateAllPieces = async () => {
    setIsBatchGenerating(true);
    setProgress(0);
    
    const total = PIECES.length * COLORS.length;
    let completed = 0;

    for (const color of COLORS) {
      for (const piece of PIECES) {
        await generatePiece(piece, color);
        completed++;
        setProgress((completed / total) * 100);
        // Small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }

    setIsBatchGenerating(false);
    toast({
      title: "Generation Complete",
      description: `Generated ${completed} chess pieces!`
    });
  };

  const downloadPiece = (key: string) => {
    const piece = generatedPieces[key];
    if (!piece?.image) return;

    const link = document.createElement('a');
    link.href = piece.image;
    link.download = `${piece.color.toLowerCase()}-${piece.piece.toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAll = () => {
    Object.keys(generatedPieces).forEach(key => {
      setTimeout(() => downloadPiece(key), 500);
    });
  };

  const getKey = (color: string, piece: string): PieceKey => `${color}-${piece}` as PieceKey;

  return (
    <ChessAppLayout>
      <div className="container mx-auto py-8 px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">AI Chess Piece Generator</h1>
          <p className="text-muted-foreground">
            Generate professional chess pieces using AI with transparent backgrounds
          </p>
        </div>

        {/* Controls */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Generation Controls
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={generateAllPieces} 
                disabled={isBatchGenerating}
                size="lg"
              >
                {isBatchGenerating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate All Pieces
                  </>
                )}
              </Button>
              
              {Object.keys(generatedPieces).length > 0 && (
                <Button onClick={downloadAll} variant="outline" size="lg">
                  <Download className="mr-2 h-4 w-4" />
                  Download All
                </Button>
              )}
            </div>

            {isBatchGenerating && (
              <div className="space-y-2">
                <Progress value={progress} className="h-2" />
                <p className="text-sm text-muted-foreground">
                  Generating pieces... {Math.round(progress)}%
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Piece Grid */}
        {COLORS.map(color => (
          <div key={color} className="mb-8">
            <h2 className="text-xl font-semibold mb-4">{color} Pieces</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {PIECES.map(piece => {
                const key = getKey(color, piece);
                const generated = generatedPieces[key];
                const isCurrentlyGenerating = isGenerating === key;

                return (
                  <Card key={key} className="overflow-hidden">
                    <CardContent className="p-4">
                      {/* Preview area with checkered background */}
                      <div 
                        className="relative aspect-square mb-3 rounded-lg overflow-hidden"
                        style={{
                          background: `
                            repeating-conic-gradient(
                              hsl(var(--muted)) 0% 25%, 
                              hsl(var(--background)) 0% 50%
                            ) 50% / 20px 20px`
                        }}
                      >
                        {generated?.image ? (
                          <img 
                            src={generated.image} 
                            alt={`${color} ${piece}`}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                            {isCurrentlyGenerating ? (
                              <Loader2 className="h-8 w-8 animate-spin" />
                            ) : (
                              <span className="text-4xl opacity-30">♟</span>
                            )}
                          </div>
                        )}

                        {generated && (
                          <div className="absolute top-2 right-2">
                            <Check className="h-5 w-5 text-green-500 bg-background rounded-full p-0.5" />
                          </div>
                        )}
                      </div>

                      <p className="text-sm font-medium text-center mb-2">{piece}</p>

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => generatePiece(piece, color)}
                          disabled={isCurrentlyGenerating || isBatchGenerating}
                        >
                          {isCurrentlyGenerating ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : generated ? (
                            <RefreshCw className="h-3 w-3" />
                          ) : (
                            <Sparkles className="h-3 w-3" />
                          )}
                        </Button>
                        
                        {generated && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => downloadPiece(key)}
                          >
                            <Download className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}

        {/* Instructions */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>How to Use Generated Pieces</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none">
            <ol className="space-y-2">
              <li>Click "Generate All Pieces" to create all 12 chess pieces</li>
              <li>Review the generated pieces on the checkered preview background</li>
              <li>Regenerate individual pieces if needed by clicking the refresh button</li>
              <li>Download pieces individually or all at once</li>
              <li>Place the downloaded PNGs in <code>src/assets/pieces/ai-*</code></li>
              <li>Update <code>src/lib/pieces.ts</code> to include the new piece style</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </ChessAppLayout>
  );
}
